import { MessageSquare, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UpdatesSummaryData } from '@/types/orderUpdates';
import type { UpdatesTimeline } from './useUpdatesTimeline';
import { formatUpdateShort, localDigits, pickLang } from './updatesFormat';

interface UpdatesSummaryProps {
  timeline: UpdatesTimeline;
}

const LINE = 'm-0 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[13px]';

/** "Did Boxy reach my customer, and what's the latest?" — answered before any row is read. */
export function UpdatesSummary({ timeline }: UpdatesSummaryProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const { counts, down, lastCall, answered } = timeline;
  const n = (text: string) => localDigits(text, lang);

  const callsLine = down.includes('calls')
    ? String(t('updates_summary_calls_unavailable'))
    : !lastCall
      ? String(t('updates_no_calls_yet'))
      : [
          String(t('updates_summary_calls', { count: counts.calls })),
          String(t('updates_summary_answered', { count: answered })),
          pickLang(lastCall.label, lang)
            ? String(
                t('updates_summary_last', {
                  label: pickLang(lastCall.label, lang),
                  when: formatUpdateShort(lastCall.at, lang),
                }),
              )
            : String(t('updates_summary_last_at', { when: formatUpdateShort(lastCall.at, lang) })),
        ].join(' · ');

  return (
    <div className='min-w-0 flex-1 space-y-2'>
      <p className={`${LINE} font-medium text-sd-foreground`}>
        <Phone className='h-4 w-4 shrink-0 text-sd-muted-foreground' strokeWidth={2} aria-hidden />
        <span className='min-w-0 tabular-nums'>{n(callsLine)}</span>
      </p>
      <p className={`${LINE} gap-x-4 text-sd-muted-foreground`}>
        {down.includes('comments') ? null : (
          <span className='inline-flex items-center gap-1.5 tabular-nums'>
            <MessageSquare className='h-4 w-4 shrink-0' strokeWidth={2} aria-hidden />
            {n(String(t('updates_summary_comments', { count: counts.comments })))}
          </span>
        )}
        {down.includes('whatsapp') ? null : (
          <span className='inline-flex items-center gap-1.5 tabular-nums'>
            <span aria-hidden className='h-2 w-2 shrink-0 rounded-full bg-[#25D366]' />
            {n(String(t('updates_summary_whatsapp', { count: counts.whatsapp })))}
          </span>
        )}
      </p>
    </div>
  );
}

// last_call.status → its label; an unknown status falls back to "Last call: when".
const CALL_STATUS: Record<string, string> = {
  answered: 'updates_call_status_answered',
  no_answer: 'updates_call_status_no_answer',
  busy: 'updates_call_status_busy',
};

/**
 * BOXY-587 — Core's own summary of the order, under the filters: one counters
 * line, then what happened in the UI language ("Summary coming soon" until the
 * text is ready).
 */
export function UpdatesBriefing({ summary }: { summary: UpdatesSummaryData }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const { counts, last_call: last, text } = summary;
  const statusKey = last ? CALL_STATUS[String(last.status).toLowerCase()] : undefined;
  const when = last ? formatUpdateShort(last.at, lang) : '';
  const lastPart = !when
    ? null
    : statusKey
      ? String(t('updates_summary_last', { label: String(t(statusKey)), when }))
      : String(t('updates_summary_last_at', { when }));
  const line = [
    String(t('updates_summary_calls', { count: counts.calls ?? 0 })),
    String(t('updates_summary_answered', { count: counts.answered ?? 0 })),
    lastPart,
    String(t('updates_summary_comments', { count: counts.comments ?? 0 })),
    String(t('updates_summary_whatsapp', { count: counts.whatsapp ?? 0 })),
  ]
    .filter(Boolean)
    .join(' · ');
  const story = (lang.startsWith('ar') ? text?.ar : text?.en)?.trim();

  return (
    <section
      aria-label={String(t('updates_summary_title'))}
      className='space-y-1 rounded-xl bg-sd-muted/40 px-4 py-3'
    >
      <p className='m-0 text-[13px] font-medium tabular-nums text-sd-foreground'>
        {localDigits(line, lang)}
      </p>
      <p dir='auto' className='m-0 text-start text-[13px] text-sd-muted-foreground'>
        {story || String(t('updates_summary_pending'))}
      </p>
    </section>
  );
}

export function UpdatesSummarySkeleton() {
  return (
    <div className='min-w-0 flex-1 space-y-2.5' aria-hidden>
      <div className='h-4 w-3/4 max-w-md animate-pulse rounded bg-sd-muted' />
      <div className='h-4 w-1/2 max-w-xs animate-pulse rounded bg-sd-muted' />
    </div>
  );
}
